// HUD arcade : niveau, gain en cours, score total, et l'annonce du bandit manchot au-dessus du gain
import { $, show, hide } from './dom.js';

// Triple au bandit manchot (celebrateScore) : l'encart du score clignote en inversion vidéo, puis le score monte
const BLINKS = 4; // nombre de clignotements (fond clair, score sombre)
const BLINK_MS = 140; // durée de chaque moitié de clignotement
const COUNT_MS = 1400; // montée du score, du premier au dernier point
const MAX_COUNT_STEPS = 40; // au-delà, le score monte par paliers (jamais plus de 40 « tic »)
const LANDED_MS = 400; // rebond final du score posé
// À partir de LONG_DIGITS chiffres, le nombre passe en plus petit (.is-long) pour tenir dans son encart sur un téléphone
export const LONG_DIGITS = 5;

/** Affiche un nombre du scoreboard, en plus petit s'il est long. */
function showNumber(el, value) {
  el.innerText = value;
  el.classList.toggle('is-long', String(value).length >= LONG_DIGITS);
}

/**
 * @param {object} options
 * @param {(key: string, params?: object) => string} options.t
 * @param {(cb: () => void, ms: number) => unknown} [options.wait]  injectable pour les tests
 * @param {() => boolean} [options.reducedMotion]
 */
export function createScoreboard({
  t,
  wait = (cb, ms) => setTimeout(cb, ms),
  reducedMotion = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false,
}) {
  const board = $('scoreboard');
  const level = $('sb-level');
  const gain = $('sb-gain');
  const score = $('sb-score');
  const scoreCell = score.closest('.score-cell');
  const tag = $('gainTag');
  let celebrating = false; // le score monte : update() ne le touche pas

  /** Pastille au-dessus du gain : le gain multiplié par les paires (un triple, lui, fait monter le score directement). */
  function tagText({ gainMultiplier = 1 }) {
    return gainMultiplier > 1 ? t('scoreboard.multiplier', { multiplier: gainMultiplier }) : '';
  }

  const pause = (ms) => new Promise((resolve) => { wait(resolve, ms); });

  /**
   * Triple : l'encart du score clignote en inversion vidéo (fond clair, score sombre), puis le score monte de `from` à `to`,
   * point par point (par paliers au-delà de MAX_COUNT_STEPS), et se pose d'un petit rebond. Le chenillard s'arrête le temps de
   * la séquence. Sans mouvement (préférence système), le score prend directement sa valeur.
   * @param {(value: number) => void} [onStep]  à chaque palier de la montée (le « tic » du compteur)
   * @returns {Promise<void>} résolue quand le score est posé
   */
  async function celebrateScore(from, to, onStep = () => {}) {
    if (reducedMotion() || to <= from) {
      showNumber(score, to);
      return;
    }
    celebrating = true;
    const chasing = board.classList.contains('is-chasing');
    board.classList.remove('is-chasing');
    showNumber(score, from);
    for (let i = 0; i < BLINKS; i++) {
      scoreCell.classList.add('is-inverted');
      await pause(BLINK_MS);
      scoreCell.classList.remove('is-inverted');
      await pause(BLINK_MS);
    }
    scoreCell.classList.add('is-counting');
    const steps = Math.min(to - from, MAX_COUNT_STEPS);
    for (let k = 1; k <= steps; k++) {
      await pause(COUNT_MS / steps);
      const value = from + Math.round(((to - from) * k) / steps);
      showNumber(score, value);
      onStep(value);
    }
    scoreCell.classList.replace('is-counting', 'is-landed');
    await pause(LANDED_MS);
    scoreCell.classList.remove('is-landed');
    board.classList.toggle('is-chasing', chasing);
    celebrating = false;
  }

  return {
    update(state) {
      level.innerText = state.currentLevel;
      showNumber(gain, state.currentGain);
      if (!celebrating) showNumber(score, state.totalScore);
      const text = tagText(state);
      if (!text) {
        hide(tag);
        tag.textContent = '';
      } else if (text !== tag.textContent) {
        tag.textContent = text;
        show(tag);
        // Nouvelle annonce : la pastille rebondit de nouveau
        tag.style.animation = 'none';
        void tag.offsetWidth;
        tag.style.animation = '';
      }
    },
    celebrateScore,
    /** Chenillard de flipper : les encarts s'allument l'un après l'autre, tant que le bandit manchot est affiché. */
    setChasing(on) {
      board.classList.toggle('is-chasing', on);
    },
  };
}
